import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import StatusBadge from "@/components/StatusBadge";
import SourceDocuments from "@/components/SourceDocuments";
import api, { apiError } from "@/lib/api";
import { RFQ_STATUSES, RFQ_STAGES, SUPPLIER_CATEGORIES, matchSuppliers, labelFor, UNQUOTED_REASONS } from "@/lib/constants";
import { CategoryCombo, BrandCombo, LinkBrandToggle, linkBrand } from "@/components/SkuFields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  ArrowLeft, Sparkles, Plus, Trash2, FileDown, Loader2, Layers, List,
  Check, ChevronDown, CircleDot, Crown, Send, ExternalLink, FolderOpen, Pencil,
  Calculator, GitCompare, FileText, Ship, Tag, X, Copy, Lock,
} from "lucide-react";
import DuplicateRfqButton from "@/components/DuplicateRfqButton";
import StockReqHeader from "@/components/StockReqHeader";
import { StockLineToggle } from "@/components/StockLineToggle";
import { BulkSkuLinkDialog } from "@/components/BulkSkuLinkDialog";

const LEGAL = /\b(limited|ltd|plc|inc|llc|co|company|corporation|corp)\.?$/i;
const folderPart = (s) => {
  let v = (s || "").trim();
  while (LEGAL.test(v)) v = v.replace(LEGAL, "").trim();
  return v.toUpperCase().replace(/[^A-Z0-9]+/g, "-").replace(/^-+|-+$/g, "");
};
const rfqFolderName = (r) => [r.code, folderPart(r.client_ref), folderPart(r.client_short_name || r.client_name), r.end_user ? folderPart(r.end_client_short_name || r.end_user) : ""].filter(Boolean).join("_");
import { toast } from "sonner";
import QuotesPanel from "@/pages/QuotesPanel";
import ComparePanel from "@/pages/ComparePanel";
import RFQWorkflow from "@/components/RFQWorkflow";
import ClientQuotePanel from "@/pages/ClientQuotePanel";
import { SupplierSends } from "@/components/SupplierSends";
import ActivityLog from "@/components/ActivityLog";
import SendToSuppliers from "@/pages/SendToSuppliers";

const INPUT_YELLOW_RFQ = "bg-[#FFFBEB] border-[#EAD9A0] focus-visible:ring-[#E0B84B]";

function AutoField({ value, onChange, onBlur, placeholder, testid, mono }) {
  const ref = React.useRef(null);
  const resize = () => {
    const el = ref.current;
    if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight}px`; }
  };
  React.useEffect(() => { resize(); }, [value]);
  return (
    <textarea
      ref={ref}
      rows={1}
      value={value || ""}
      placeholder={placeholder}
      onChange={(e) => { onChange(e); resize(); }}
      onBlur={onBlur}
      data-testid={testid}
      className={`w-full resize-none overflow-hidden rounded-md border border-transparent bg-transparent px-2 py-1.5 text-sm leading-snug hover:border-input focus:border-input focus:outline-none ${mono ? "font-mono text-xs" : ""}`}
    />
  );
}


const LINK_STATUSES = [
  { value: "sent", label: "Sent RFQ" },
  { value: "quoted", label: "Quote Received" },
  { value: "costing_done", label: "Costing Done" },
  { value: "declined", label: "RFQ Declined" },
];
const LINK_STYLES = {
  sent: "bg-[#E6ECF5] text-[#1B4383] border-[#C9D8EC]",
  quoted: "bg-[#E4EEE8] text-[#527D63] border-[#527D63]",
  costing_done: "bg-[#DCE8F5] text-[#1B4383] border-[#1B4383] font-semibold",
  declined: "bg-slate-100 text-slate-400 border-slate-200 line-through",
};

function readFileBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function SupplierChip({ link, onStatus, onEdit, onRemove, onOpenWorkspace }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${LINK_STYLES[link.status]} ${link.awarded ? "ring-2 ring-[#527D63] ring-offset-1" : ""}`}
          data-testid={`chip-${link.id}`}
        >
          {link.awarded && <Crown className="h-3 w-3 text-[#527D63]" />}
          {(link.status === "quoted" || link.status === "costing_done") && !link.awarded && <Check className="h-3 w-3" />}
          {link.supplier_name}
          {link.quoted_price != null && <span className="ml-1 opacity-70">· ${link.quoted_price}</span>}
          <ChevronDown className="h-3 w-3 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-52">
        <DropdownMenuLabel className="text-xs">{link.supplier_name}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {LINK_STATUSES.map((s) => (
          <DropdownMenuItem key={s.value} disabled={link.status === s.value} onClick={() => onStatus(link, s.value)} data-testid={`set-${s.value}-${link.id}`}>
            {link.status === s.value ? <Check className="mr-2 h-4 w-4 text-[#527D63]" /> : <CircleDot className="mr-2 h-4 w-4 opacity-30" />}
            Mark {s.label}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onOpenWorkspace(link)} data-testid={`open-workspace-${link.id}`}>
          <ExternalLink className="mr-2 h-4 w-4" /> Open quote workspace
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onEdit(link)} data-testid={`edit-notes-${link.id}`}>
          <Pencil className="mr-2 h-4 w-4" /> Notes
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onRemove(link)} className="text-destructive focus:text-destructive" data-testid={`remove-link-${link.id}`}>
          <Trash2 className="mr-2 h-4 w-4" /> Remove
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function AddSupplier({ description, suppliers, attachedIds, onAdd, onCreate, testid }) {
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const available = suppliers.filter((s) => !attachedIds.includes(s.id));
  const { matched, rest } = matchSuppliers(description, available);
  const restSuppliers = rest.filter((s) => (s.partner_type || "supplier") !== "oem");
  const restOems = rest.filter((s) => (s.partner_type || "supplier") === "oem");
  const OemTag = () => <span className="ml-2 rounded-full bg-[#E6ECF5] px-1.5 py-0.5 text-[10px] font-medium text-[#1B4383]">OEM</span>;
  const doCreate = async () => { if (!newName.trim() || !onCreate) return; setCreating(true); try { await onCreate(newName.trim()); setNewName(""); } finally { setCreating(false); } };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="inline-flex items-center gap-1 rounded-full border border-dashed border-slate-300 px-2 py-0.5 text-xs font-medium text-slate-500 hover:border-primary hover:text-primary" data-testid={testid}>
          <Plus className="h-3 w-3" /> Add
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-80 w-64 overflow-y-auto">
        <div className="p-1.5" onKeyDown={(e) => e.stopPropagation()}>
          <div className="mb-1 text-xs font-medium text-slate-500">Add a new supplier</div>
          <div className="flex items-center gap-1">
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") { e.preventDefault(); doCreate(); } }} placeholder="Supplier name…" className="h-7 text-xs" data-testid={`${testid}-new-name`} />
            <Button size="sm" className="h-7 shrink-0 px-2 text-xs" disabled={creating || !newName.trim()} data-testid={`${testid}-create`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); doCreate(); }}>{creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Create"}</Button>
          </div>
        </div>
        <DropdownMenuSeparator />
        {matched.length > 0 && (
          <>
            <DropdownMenuLabel className="text-xs text-primary">Suggested</DropdownMenuLabel>
            {matched.map((s) => <DropdownMenuItem key={s.id} onClick={() => onAdd(s.id)}>{s.name}{(s.partner_type === "oem") && <OemTag />}</DropdownMenuItem>)}
            <DropdownMenuSeparator />
          </>
        )}
        {restSuppliers.length > 0 && (
          <>
            <DropdownMenuLabel className="text-xs">Suppliers</DropdownMenuLabel>
            {restSuppliers.map((s) => <DropdownMenuItem key={s.id} onClick={() => onAdd(s.id)}>{s.name}</DropdownMenuItem>)}
          </>
        )}
        {restOems.length > 0 && (
          <>
            {restSuppliers.length > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-xs">OEM Partners</DropdownMenuLabel>
            {restOems.map((s) => <DropdownMenuItem key={s.id} onClick={() => onAdd(s.id)}>{s.name}<OemTag /></DropdownMenuItem>)}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ItemRow({ item, index, suppliers, onSaveFields, onAddSupplier, onCreateSupplier, onStatus, onEdit, onRemoveLink, onDelete, onOpenWorkspace, onOpenSku, onLinkSku, catalogById, selected, onToggleSelect, onReload }) {
  const [local, setLocal] = useState(item);
  useEffect(() => setLocal(item), [item.id]);
  const commit = () => onSaveFields(item.id, {
    description: local.description || "",
    quantity: local.quantity === "" || local.quantity == null ? null : Number(local.quantity),
    unit: local.unit || "",
    spec_or_part: local.spec_or_part || "",
  });
  const attachedIds = (item.suppliers || []).map((s) => s.supplier_id);

  return (
    <tr className="border-t border-border align-top hover:bg-slate-50" data-testid={`item-row-${item.id}`}>
      <td className="px-3 py-3 text-center align-top w-10">
        <Checkbox checked={selected} onCheckedChange={() => onToggleSelect(item.id)} data-testid={`select-item-${item.id}`} aria-label="Select line" />
      </td>
      <td className="px-3 py-3 text-center font-mono text-xs text-muted-foreground w-10">{index}</td>
      <td className="px-2 py-2 min-w-[16rem]">
        <AutoField value={local.description} onChange={(e) => setLocal({ ...local, description: e.target.value })}
          onBlur={commit} placeholder="Item description" testid={`item-desc-${item.id}`} />
      </td>
      <td className="px-2 py-2 w-56 align-top">
        <AutoField value={local.spec_or_part} onChange={(e) => setLocal({ ...local, spec_or_part: e.target.value })}
          onBlur={commit} placeholder="—" mono testid={`item-spec-${item.id}`} />
      </td>
      <td className="px-2 py-2 w-32">
        <div className="flex items-center gap-1">
          <Input type="number" value={local.quantity ?? ""} onChange={(e) => setLocal({ ...local, quantity: e.target.value })}
            onBlur={commit} className="h-8 w-20 text-right font-mono border-transparent bg-transparent hover:border-input focus:border-input" data-testid={`item-qty-${item.id}`} />
          <Input value={local.unit || ""} onChange={(e) => setLocal({ ...local, unit: e.target.value })}
            onBlur={commit} placeholder="unit" className="h-8 w-14 text-xs border-transparent bg-transparent hover:border-input focus:border-input" data-testid={`item-unit-${item.id}`} />
        </div>
      </td>
      <td className="px-2 py-2 w-44 align-top">
        {item.catalog_sku ? (
          <div className="flex flex-col items-start gap-1">
            <span className="inline-flex max-w-full items-center gap-1 rounded bg-[#E6ECF5] px-1.5 py-0.5 font-mono text-[10px] font-semibold text-[#1B4383]" title={item.catalog_name || item.catalog_sku} data-testid={`item-sku-${item.id}`}>
              <span className="truncate">{item.catalog_sku}</span>
              <button onClick={() => onLinkSku(item.id, "")} className="shrink-0 text-[#1B4383]/60 hover:text-destructive" data-testid={`item-sku-unlink-${item.id}`}><X className="h-3 w-3" /></button>
            </span>
          </div>
        ) : (
          <button onClick={() => onOpenSku(item)} className="inline-flex items-center gap-1 rounded border border-dashed border-amber-300 bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-700 hover:bg-amber-100" data-testid={`item-sku-link-${item.id}`}><Tag className="h-3 w-3" />Link SKU</button>
        )}
      </td>
      <td className="px-2 py-2">
        <div className="flex flex-wrap items-center gap-1.5" data-testid={`item-suppliers-${item.id}`}>
          {item.line_type === "from_stock" ? <StockLineToggle item={item} onChanged={onReload} /> : (<>
          {(item.suppliers || []).map((l) => (
            <SupplierChip key={l.id} link={l} onStatus={onStatus} onEdit={onEdit} onRemove={onRemoveLink} onOpenWorkspace={onOpenWorkspace} />
          ))}
          <AddSupplier description={item.description} suppliers={suppliers} attachedIds={attachedIds}
            onAdd={(sid) => onAddSupplier(item.id, sid)} onCreate={(name) => onCreateSupplier?.(item.id, name)} testid={`add-supplier-${item.id}`} />
          {onReload && item.catalog_sku && <StockLineToggle item={item} onChanged={onReload} />}
          </>)}
        </div>
      </td>
      <td className="px-2 py-2 text-right w-8">
        <button onClick={() => onDelete(item.id)} className="text-slate-400 hover:text-destructive" data-testid={`item-delete-${item.id}`}><Trash2 className="h-4 w-4" /></button>
      </td>
    </tr>
  );
}

function ItemCard({ item, index, suppliers, onSaveFields, onAddSupplier, onCreateSupplier, onStatus, onEdit, onRemoveLink, onDelete, onOpenWorkspace, onOpenSku, onLinkSku, selected, onToggleSelect, onReload }) {
  const [local, setLocal] = useState(item);
  useEffect(() => setLocal(item), [item.id]);
  const commit = () => onSaveFields(item.id, {
    description: local.description || "",
    quantity: local.quantity === "" || local.quantity == null ? null : Number(local.quantity),
    unit: local.unit || "",
    spec_or_part: local.spec_or_part || "",
  });
  const attachedIds = (item.suppliers || []).map((s) => s.supplier_id);
  return (
    <div className="p-4" data-testid={`item-card-${item.id}`}>
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Checkbox checked={selected} onCheckedChange={() => onToggleSelect(item.id)} data-testid={`select-item-m-${item.id}`} aria-label="Select line" />
          <span className="font-mono text-xs text-muted-foreground">#{index}</span>
        </div>
        <button onClick={() => onDelete(item.id)} className="text-slate-400 hover:text-destructive" data-testid={`item-delete-m-${item.id}`}><Trash2 className="h-4 w-4" /></button>
      </div>
      <div className="space-y-3">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Item Description</label>
          <div className="rounded-md border border-input">
            <AutoField value={local.description} onChange={(e) => setLocal({ ...local, description: e.target.value })}
              onBlur={commit} placeholder="Item description" testid={`item-desc-m-${item.id}`} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Part Number</label>
            <div className="rounded-md border border-input">
              <AutoField value={local.spec_or_part} onChange={(e) => setLocal({ ...local, spec_or_part: e.target.value })}
                onBlur={commit} placeholder="—" mono testid={`item-spec-m-${item.id}`} />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Quantity</label>
            <div className="flex items-center gap-1">
              <Input type="number" value={local.quantity ?? ""} onChange={(e) => setLocal({ ...local, quantity: e.target.value })}
                onBlur={commit} className="h-9 w-full text-right font-mono" data-testid={`item-qty-m-${item.id}`} />
              <Input value={local.unit || ""} onChange={(e) => setLocal({ ...local, unit: e.target.value })}
                onBlur={commit} placeholder="unit" className="h-9 w-16 text-xs" data-testid={`item-unit-m-${item.id}`} />
            </div>
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Catalog SKU</label>
          <div>
            {item.catalog_sku ? (
              <span className="inline-flex items-center gap-1 rounded bg-[#E6ECF5] px-2 py-1 font-mono text-[11px] font-semibold text-[#1B4383]" data-testid={`item-sku-m-${item.id}`}>
                {item.catalog_sku}
                <button onClick={() => onLinkSku(item.id, "")} className="text-[#1B4383]/60 hover:text-destructive" data-testid={`item-sku-unlink-m-${item.id}`}><X className="h-3 w-3" /></button>
              </span>
            ) : (
              <button onClick={() => onOpenSku(item)} className="inline-flex items-center gap-1 rounded border border-dashed border-amber-300 bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-700 hover:bg-amber-100" data-testid={`item-sku-link-m-${item.id}`}><Tag className="h-3 w-3" />Link SKU</button>
            )}
            {item.catalog_sku && onReload && item.line_type !== "from_stock" && <div className="mt-1"><StockLineToggle item={item} onChanged={onReload} /></div>}
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Suppliers</label>
          <div className="flex flex-wrap items-center gap-1.5" data-testid={`item-suppliers-m-${item.id}`}>
            {item.line_type === "from_stock" ? <StockLineToggle item={item} onChanged={onReload} /> : (<>
            {(item.suppliers || []).map((l) => (
              <SupplierChip key={l.id} link={l} onStatus={onStatus} onEdit={onEdit} onRemove={onRemoveLink} onOpenWorkspace={onOpenWorkspace} />
            ))}
            <AddSupplier description={item.description} suppliers={suppliers} attachedIds={attachedIds}
              onAdd={(sid) => onAddSupplier(item.id, sid)} onCreate={(name) => onCreateSupplier?.(item.id, name)} testid={`add-supplier-m-${item.id}`} />
            </>)}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RFQDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const mainTab = searchParams.get("tab") || "items";
  const setMainTab = (v) => setSearchParams(v === "items" ? {} : { tab: v }, { replace: true });
  const [rfq, setRfq] = useState(null);
  const [uqOpen, setUqOpen] = useState(false);
  const [allClients, setAllClients] = useState([]);
  useEffect(() => { api.get("/clients").then((r) => setAllClients(r.data)).catch(() => {}); }, []);
  const saveRfqField = async (patch) => {
    try { const { data } = await api.put(`/rfqs/${id}`, { ...rfq, ...patch }); setRfq(data); bumpActivity(); return true; } catch (e) { toast.error(apiError(e)); return false; }
  };
  const _didInitScroll = React.useRef(false);
  useEffect(() => {
    if (rfq && !_didInitScroll.current) {
      _didInitScroll.current = true;
      const t = searchParams.get("tab");
      if (t && t !== "items") {
        setTimeout(() => document.querySelector('[data-testid="rfq-main-tabs"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
      }
    }
  }, [rfq, searchParams]);
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [skuPick, setSkuPick] = useState(null);
  const [bulkSku, setBulkSku] = useState(false);
  const linkSkuQuiet = async (itemId, productId) => {
    try {
      const { data } = await api.put(`/items/${itemId}/catalog`, { catalog_sku_id: productId });
      setItems((prev) => prev.map((it) => (it.id === itemId ? data : it)));
      return true;
    } catch (e) { toast.error(apiError(e)); return false; }
  };
  const [skuSearch, setSkuSearch] = useState("");
  const [skuCreate, setSkuCreate] = useState(false);
  const [newProd, setNewProd] = useState({ name: "", category: "", uom: "EA", brand: "", mpn: "", default_supplier_id: "", link_brand: true });
  const tc = (s) => (s || "").replace(/\S+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));
  const [suppliers, setSuppliers] = useState([]);
  const [tab, setTab] = useState("text");
  const [emailText, setEmailText] = useState("");
  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [grouped, setGrouped] = useState(false);
  const [quoteDialog, setQuoteDialog] = useState(null); // {link}
  const [wonOpen, setWonOpen] = useState(false);
  const [wonForm, setWonForm] = useState({ client_po_ref: "", client_po_date: "" });
  const [wonBusy, setWonBusy] = useState(false);
  const [srcKey, setSrcKey] = useState(0);
  const [activityKey, setActivityKey] = useState(0);
  const [order, setOrder] = useState(null);
  const bumpActivity = () => setActivityKey((k) => k + 1);
  const [sendOpen, setSendOpen] = useState(false);
  const [odEditing, setOdEditing] = useState(false);
  const [odValue, setOdValue] = useState("");
  const [odSaving, setOdSaving] = useState(false);
  const saveOnedrive = async () => {
    const v = odValue.trim();
    setOdSaving(true);
    try {
      const { data } = await api.put(`/rfqs/${id}`, { ...rfq, onedrive_url: v });
      setRfq(data); setOdEditing(false); bumpActivity();
      toast.success(v ? "Folder link saved" : "Folder link removed");
    } catch (e) { toast.error(apiError(e)); } finally { setOdSaving(false); }
  };
  const [members, setMembers] = useState([]);
  const copyAck = async () => {
    try {
      const { data } = await api.get(`/rfqs/${id}/acknowledge/draft`);
      try { await navigator.clipboard.writeText(data.message); toast.success("Acknowledgement Draft Copied — Paste It Into Your Reply"); } catch { window.prompt("Copy The Acknowledgement Draft", data.message); }
      if (!rfq?.acknowledged) { await api.post(`/rfqs/${id}/acknowledge`); bumpActivity(); refreshRfq(); }
    } catch (e) { toast.error(apiError(e)); }
  };

  const [shipment, setShipment] = useState(null);
  const [bulkName, setBulkName] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [selBulkName, setSelBulkName] = useState("");
  const toggleSelect = (itemId) => setSelectedIds((prev) => { const n = new Set(prev); if (n.has(itemId)) n.delete(itemId); else n.add(itemId); return n; });
  const clearSelection = () => setSelectedIds(new Set());

  const loadItems = useCallback(() => api.get(`/rfqs/${id}/items`).then((r) => setItems(r.data)), [id]);
  const refreshRfq = useCallback(() => api.get(`/rfqs/${id}`).then((r) => setRfq(r.data)), [id]);

  useEffect(() => {
    api.get(`/rfqs/${id}`).then((r) => setRfq(r.data)).catch(() => navigate("/rfqs"));
    api.get("/suppliers").then((r) => setSuppliers(r.data));
    api.get("/catalog/products").then((r) => setCatalog(r.data)).catch(() => {});
    api.get("/team-members").then((r) => setMembers(r.data)).catch(() => {});
    api.get(`/rfqs/${id}/order`).then((r) => setOrder(r.data)).catch(() => {});
    loadItems();
  }, [id, loadItems, navigate]);

  useEffect(() => {
    if (order?.id) api.get("/shipments").then((r) => setShipment((r.data || []).find((s) => s.order_id === order.id) || null)).catch(() => {});
  }, [order]);

  const setOwner = async (owner_id) => {
    const { data } = await api.put(`/rfqs/${id}`, { ...rfq, owner_id });
    setRfq(data); bumpActivity();
    toast.success("RFQ owner updated");
  };

  const setStage = async (stage) => { const { data } = await api.put(`/rfqs/${id}`, { ...rfq, stage }); setRfq(data); bumpActivity(); };
  const confirmWon = async (goToOrder = false) => {
    setWonBusy(true);
    try {
      await api.put(`/rfqs/${id}`, { ...rfq, stage: "won" });
      const { data: orders } = await api.get("/orders");
      const ord = (orders || []).find((o) => o.rfq_id === id);
      if (ord && (wonForm.client_po_ref || wonForm.client_po_date)) {
        await api.put(`/orders/${ord.id}`, wonForm);
      }
      setWonOpen(false); setWonForm({ client_po_ref: "", client_po_date: "" });
      if (goToOrder && ord) { navigate(`/orders/${ord.id}`, { state: { ackPo: true } }); return; }
      const load = await api.get(`/rfqs/${id}`); setRfq(load.data); bumpActivity();
      if (ord) setOrder(ord);
      toast.success("Marked Won — order created", ord ? { action: { label: "Open order", onClick: () => navigate(`/orders/${ord.id}`) } } : undefined);
    } catch (e) { toast.error(apiError(e)); } finally { setWonBusy(false); }
  };

  const extract = async () => {
    setExtracting(true);
    try {
      let payload;
      if (tab === "text") {
        if (!emailText.trim()) { toast.error("Paste the RFQ email body first."); setExtracting(false); return; }
        payload = { source_type: "text", text: emailText };
      } else {
        if (!file) { toast.error("Select a file first."); setExtracting(false); return; }
        const b64 = await readFileBase64(file);
        payload = { source_type: tab, file_base64: b64 };
        // Keep the original uploaded document on file so it can be referenced later.
        api.post(`/rfqs/${id}/source-files`, { filename: file.name, content_type: file.type, file_base64: b64 })
          .then(() => setSrcKey((k) => k + 1)).catch(() => {});
      }
      const { data } = await api.post(`/rfqs/${id}/extract`, payload);
      toast.success(`Extracted ${data.items.length} line item(s)`);
      setEmailText(""); setFile(null); loadItems(); refreshRfq();
    } catch (e) { toast.error(apiError(e)); } finally { setExtracting(false); }
  };

  const saveItemFields = async (itemId, patch) => {
    const { data } = await api.put(`/items/${itemId}`, patch);
    setItems((prev) => prev.map((it) => (it.id === itemId ? data : it)));
  };
  const linkSku = async (itemId, productId) => {
    try {
      const { data } = await api.put(`/items/${itemId}/catalog`, { catalog_sku_id: productId });
      setItems((prev) => prev.map((it) => (it.id === itemId ? data : it)));
      setSkuPick(null); setSkuSearch(""); setSkuCreate(false);
      if (productId) toast.success(`Linked ${data.catalog_sku}`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const startSkuCreate = () => {
    setNewProd({ name: tc(skuPick?.description || ""), category: "", uom: skuPick?.unit || "EA",
      brand: "", mpn: skuPick?.spec_or_part || "", default_supplier_id: "" });
    setSkuCreate(true);
  };
  const catalogById = React.useMemo(() => Object.fromEntries(catalog.map((p) => [p.id, p])), [catalog]);
  const createAndLinkSku = async () => {
    if (!newProd.name.trim()) { toast.error("Product name is required"); return; }
    try {
      const body = { ...newProd, name: tc(newProd.name), category: tc(newProd.category), brand: tc(newProd.brand),
        default_supplier_id: newProd.default_supplier_id === "none" ? "" : newProd.default_supplier_id };
      const { data: prod } = await api.post("/catalog/products", body);
      if (newProd.link_brand && body.brand && body.default_supplier_id) await linkBrand(body.default_supplier_id, body.brand).catch(() => {});
      setCatalog((c) => [...c, prod]);
      await linkSku(skuPick.id, prod.id);
    } catch (e) { toast.error(apiError(e)); }
  };
  const addRow = async () => { const { data } = await api.post(`/rfqs/${id}/items`, { description: "" }); setItems((p) => [...p, data]); };
  const deleteRow = async (itemId) => { await api.delete(`/items/${itemId}`); setItems((p) => p.filter((it) => it.id !== itemId)); };

  const addSupplier = async (itemId, supplierId) => {
    try { await api.post(`/items/${itemId}/suppliers`, { supplier_id: supplierId }); await loadItems(); refreshRfq(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const createAndAddSupplier = async (itemId, name) => {
    try {
      const { data } = await api.post("/suppliers", { name });
      const sup = await api.get("/suppliers"); setSuppliers(sup.data);
      await api.post(`/items/${itemId}/suppliers`, { supplier_id: data.id });
      await loadItems(); refreshRfq();
      toast.success(`Added supplier "${name}"`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const applySupplierToAll = async (supplierId) => {
    if (!items.length) { toast.error("No line items yet"); return; }
    try {
      let added = 0;
      for (const it of items) {
        if (!(it.suppliers || []).some((s) => s.supplier_id === supplierId)) { await api.post(`/items/${it.id}/suppliers`, { supplier_id: supplierId }); added++; }
      }
      await loadItems(); refreshRfq();
      toast.success(`Supplier applied to ${added} line${added === 1 ? "" : "s"}`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const createAndApplyToAll = async (name) => {
    if (!name) return;
    try {
      const { data } = await api.post("/suppliers", { name });
      const sup = await api.get("/suppliers"); setSuppliers(sup.data);
      await applySupplierToAll(data.id);
    } catch (e) { toast.error(apiError(e)); }
  };
  const applySupplierToSelected = async (supplierId) => {
    const sel = items.filter((i) => selectedIds.has(i.id));
    if (!sel.length) { toast.error("Select at least one line item"); return; }
    try {
      let added = 0;
      for (const it of sel) {
        if (!(it.suppliers || []).some((s) => s.supplier_id === supplierId)) { await api.post(`/items/${it.id}/suppliers`, { supplier_id: supplierId }); added++; }
      }
      await loadItems(); refreshRfq();
      toast.success(`Supplier applied to ${added} selected line${added === 1 ? "" : "s"}`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const createAndApplyToSelected = async (name) => {
    if (!name) return;
    try {
      const { data } = await api.post("/suppliers", { name });
      const sup = await api.get("/suppliers"); setSuppliers(sup.data);
      await applySupplierToSelected(data.id);
    } catch (e) { toast.error(apiError(e)); }
  };
  const removeSupplierFromSelected = async (supplierId) => {
    const sel = items.filter((i) => selectedIds.has(i.id));
    try {
      let removed = 0;
      for (const it of sel) {
        const link = (it.suppliers || []).find((s) => s.supplier_id === supplierId);
        if (link) { await api.delete(`/item-suppliers/${link.id}`); removed++; }
      }
      await loadItems(); refreshRfq();
      toast.success(`Removed supplier from ${removed} selected line${removed === 1 ? "" : "s"}`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const removeAllFromSelected = async () => {
    const sel = items.filter((i) => selectedIds.has(i.id));
    try {
      let removed = 0;
      for (const it of sel) {
        for (const link of (it.suppliers || [])) { await api.delete(`/item-suppliers/${link.id}`); removed++; }
      }
      await loadItems(); refreshRfq();
      toast.success(`Removed ${removed} supplier assignment${removed === 1 ? "" : "s"}`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const setLinkStatus = async (link, status) => {
    try { await api.put(`/item-suppliers/${link.id}`, { status }); await loadItems(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const removeLink = async (link) => { try { await api.delete(`/item-suppliers/${link.id}`); await loadItems(); } catch (e) { toast.error(apiError(e)); } };
  const saveQuote = async () => {
    try {
      await api.put(`/item-suppliers/${quoteDialog.link.id}`, {
        status: quoteDialog.link.status,
        quoted_price: quoteDialog.price === "" ? null : Number(quoteDialog.price),
        notes: quoteDialog.notes || "",
      });
      setQuoteDialog(null); await loadItems(); toast.success("Quote saved");
    } catch (e) { toast.error(apiError(e)); }
  };

  const download = async (supplierId, supplierName, format) => {
    const res = await api.get(`/rfqs/${id}/export/${supplierId}`, { params: { format }, responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = `RFQ_${rfq.title}_${supplierName}.${format === "excel" ? "xlsx" : "pdf"}`.replace(/[^a-zA-Z0-9._-]/g, "_");
    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
  };
  void download;

  if (!rfq) return <AppLayout title="RFQ"><div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div></AppLayout>;

  const assignedCount = items.filter((i) => (i.suppliers || []).length > 0).length;
  const allSelected = items.length > 0 && selectedIds.size === items.length;
  const toggleSelectAll = () => setSelectedIds(allSelected ? new Set() : new Set(items.map((i) => i.id)));
  const selectedSupplierOptions = (() => {
    const map = new Map();
    items.filter((i) => selectedIds.has(i.id)).forEach((it) => (it.suppliers || []).forEach((l) => {
      if (!map.has(l.supplier_id)) map.set(l.supplier_id, suppliers.find((s) => s.id === l.supplier_id)?.name || l.supplier_name || "Supplier");
    }));
    return [...map.entries()].map(([sid, name]) => ({ id: sid, name }));
  })();
  const _stage = rfq?.stage || "new";
  const _scrollTabs = () => document.querySelector('[data-testid="rfq-main-tabs"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
  const wfSteps = rfq?.is_stock ? [
    { key: "items", label: "Line Items", done: items.length > 0, onClick: () => { setMainTab("items"); _scrollTabs(); } },
    { key: "suppliers", label: "Send To Suppliers", done: items.length > 0 && assignedCount === items.length, onClick: () => { setMainTab("items"); _scrollTabs(); } },
    { key: "quotes", label: "Supplier Quotes", done: items.some((i) => (i.suppliers || []).some((s) => s.quoted_price != null)), onClick: () => { setMainTab("quotes"); _scrollTabs(); } },
    { key: "compare", label: "Compare & Award", done: items.length > 0 && items.every((i) => (i.suppliers || []).some((s) => s.awarded)), onClick: () => { setMainTab("compare"); _scrollTabs(); } },
    { key: "po", label: "Create Stock PO", done: _stage === "won", onClick: () => window.scrollTo({ top: 0, behavior: "smooth" }) },
  ] : [
    { key: "ack", label: "Acknowledge", done: !!rfq?.acknowledged, onClick: copyAck },
    { key: "items", label: "Line Items", done: items.length > 0, onClick: () => { setMainTab("items"); _scrollTabs(); } },
    { key: "folder", label: "Folder Link", done: !!rfq?.onedrive_url, onClick: () => { setOdValue(rfq?.onedrive_url || ""); setOdEditing(true); document.querySelector('[data-testid="rfq-onedrive-card"]')?.scrollIntoView({ behavior: "smooth", block: "center" }); } },
    { key: "suppliers", label: "Send to Suppliers", done: items.length > 0 && assignedCount === items.length, onClick: () => { setMainTab("items"); _scrollTabs(); } },
    { key: "quotes", label: "Quotes & Costing", done: ["quoted", "won"].includes(_stage), onClick: () => { setMainTab("quotes"); _scrollTabs(); } },
    { key: "compare", label: "Compare", done: ["quoted", "won"].includes(_stage), onClick: () => { setMainTab("compare"); _scrollTabs(); } },
    { key: "quote", label: "Somcore Quote", done: ["quoted", "won"].includes(_stage), onClick: () => { setMainTab("client-quote"); _scrollTabs(); } },
    { key: "outcome", label: "Outcome", done: ["won", "lost"].includes(_stage), onClick: () => window.scrollTo({ top: 0, behavior: "smooth" }) },
  ];
  const wfCurrent = wfSteps.findIndex((s) => !s.done);
  const awaitingDecision = items.filter((i) => (i.suppliers || []).length > 1).length;
  const awardedItems = items.filter((i) => (i.suppliers || []).some((s) => s.awarded)).length;

  // Build supplier groups (an item can appear under multiple suppliers)
  const groups = {};
  for (const it of items) {
    for (const l of it.suppliers || []) {
      if (!groups[l.supplier_id]) groups[l.supplier_id] = { name: l.supplier_name, categories: l.supplier_categories || (l.supplier_category ? [l.supplier_category] : []), rows: [] };
      groups[l.supplier_id].rows.push({ item: it, link: l });
    }
  }
  const unassignedItems = items.filter((i) => (i.suppliers || []).length === 0);

  return (
    <AppLayout title={rfq.title} actions={rfq.is_stock ? null : <DuplicateRfqButton rfq={rfq} />}>
      <button onClick={() => navigate("/rfqs")} className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary" data-testid="back-to-rfqs">
        <ArrowLeft className="h-4 w-4" /> Back to RFQs
      </button>
      {rfq.is_stock ? <StockReqHeader rfq={rfq} members={members} setOwner={setOwner} onChanged={refreshRfq} /> : (
      <div className="mb-6 rounded-lg border border-border bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="grid w-full grid-cols-2 gap-x-6 gap-y-2 text-sm sm:gap-x-10 md:w-auto md:grid-cols-4">
            <Meta label="Somcore Ref" value={rfq.code || "—"} mono />
            <div>
              <div className="text-xs tracking-wide text-muted-foreground">Client</div>
              {["quoted", "won", "lost"].includes(rfq.stage) ? (
                <div className="mt-0.5 flex items-center gap-1.5">
                  {rfq.client_id ? (
                    <button type="button" onClick={() => navigate(`/clients/${rfq.client_id}`, { state: { fromRfq: { id, title: rfq.title || rfq.code } } })} data-testid="rfq-client-link" title="View / edit client" className="inline-flex items-center gap-1 font-medium text-brand underline-offset-2 hover:underline">
                      {rfq.client_name}<ExternalLink className="h-3.5 w-3.5 opacity-70" />
                    </button>
                  ) : <span className="font-medium text-slate-900">{rfq.client_name}</span>}
                  <Lock className="h-3 w-3 text-slate-400" title="Locked — a client quote has been sent" data-testid="rfq-client-locked" />
                </div>
              ) : (
                <div className="mt-0.5 flex items-center gap-1">
                  <Select value={rfq.client_id || ""} onValueChange={(v) => {
                    if (v === rfq.client_id) return;
                    const nm = (allClients.find((c) => c.id === v) || {}).name || "the new client";
                    if (!window.confirm(`Change the client to ${nm}?\n\nSupplier quotes and costings stay as they are, and the client quote will be made out to ${nm}. Once a client quote is sent, the client is locked.`)) return;
                    saveRfqField({ client_id: v, end_client_id: rfq.end_client_id === v ? "" : rfq.end_client_id, client_contact_name: "", client_contact_email: "" }).then((ok) => ok && toast.success(`Client changed to ${nm}`));
                  }}>
                    <SelectTrigger className="h-8 min-w-[10rem] max-w-[14rem] border-dashed text-sm font-medium text-brand" data-testid="rfq-client-select"><SelectValue placeholder="Pick client">{rfq.client_name}</SelectValue></SelectTrigger>
                    <SelectContent className="max-h-72">{allClients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                  {rfq.client_id && <button type="button" onClick={() => navigate(`/clients/${rfq.client_id}`, { state: { fromRfq: { id, title: rfq.title || rfq.code } } })} className="p-1 text-brand" title="Open client" data-testid="rfq-client-link"><ExternalLink className="h-3.5 w-3.5" /></button>}
                </div>
              )}
            </div>
            <Meta label="Client's Ref" value={rfq.client_ref || "—"} />
            <div>
              <div className="mb-1 text-xs tracking-wide text-muted-foreground">End Client</div>
              <Select value={rfq.end_client_id || "__none__"} onValueChange={(v) => saveRfqField({ end_client_id: v === "__none__" ? "" : v, end_user: v === "__none__" ? "" : rfq.end_user })}>
                <SelectTrigger className="h-7 w-full text-sm sm:w-44" data-testid="rfq-endclient-field"><SelectValue placeholder={rfq.end_user || "None"} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{!rfq.end_client_id && rfq.end_user ? `${rfq.end_user} (Not Linked)` : "None"}</SelectItem>
                  {allClients.filter((c) => c.id !== rfq.client_id).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="mb-1 text-xs tracking-wide text-muted-foreground">Buyer</div>
              <Input
                defaultValue={rfq.buyer || ""}
                placeholder="Requestor"
                className="h-7 w-full text-sm sm:w-40"
                onBlur={(e) => { const v = e.target.value.trim(); if (v !== (rfq.buyer || "")) saveRfqField({ buyer: v }); }}
                data-testid="rfq-buyer-field"
              />
            </div>
            <Meta label="RFQ Issued Date" value={rfq.rfq_issued_date || "—"} mono />
            <Meta label="Date Received" value={rfq.date_received || "—"} mono />
            <Meta label="Quote Deadline" value={rfq.quote_deadline || "—"} mono />
            <div>
              <div className="mb-1 text-xs tracking-wide text-muted-foreground">RFQ Owner</div>
              <Select value={rfq.owner_id || ""} onValueChange={setOwner}>
                <SelectTrigger className="h-7 w-full text-sm sm:w-44" data-testid="rfq-owner-field">
                  <SelectValue placeholder="Assign owner">{rfq.owner_name || "Assign owner"}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name || m.name}{m.job_title ? ` — ${m.job_title}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div data-testid="rfq-linked-order">
              <div className="mb-1 text-xs tracking-wide text-muted-foreground">Order</div>
              {order ? (
                <div className="mt-0.5 flex flex-wrap items-center gap-2">
                  <button type="button" onClick={() => navigate(`/orders/${order.id}`)} data-testid="rfq-order-link"
                    className="inline-flex items-center gap-1.5 font-medium text-brand underline-offset-2 hover:underline">
                    {order.order_no}
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${order.status === "fulfilled" || order.status === "delivered" || order.status === "complete" ? "bg-emerald-100 text-emerald-700" : order.status === "closed" || order.status === "completed" || order.status === "cancelled" ? "bg-slate-100 text-slate-600" : "bg-[#E6ECF5] text-[#1B4383]"}`}>{(order.status || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</span>
                    <ExternalLink className="h-3.5 w-3.5 opacity-70" />
                  </button>
                  {shipment && (
                    <button type="button" onClick={() => navigate(`/logistics/shipment/${shipment.id}`)} data-testid="rfq-shipment-link"
                      className="inline-flex items-center gap-1.5 font-medium text-[#1B4383] underline-offset-2 hover:underline">
                      <Ship className="h-3.5 w-3.5" />{shipment.shipment_no}
                      <ExternalLink className="h-3.5 w-3.5 opacity-70" />
                    </button>
                  )}
                </div>
              ) : (
                <div className="mt-0.5 text-sm text-muted-foreground">Not linked yet — appears when marked <b>Won</b></div>
              )}
            </div>
            {rfq.opportunity_id && (
              <div data-testid="rfq-linked-opportunity">
                <div className="mb-1 text-xs tracking-wide text-muted-foreground">Opportunity</div>
                <button type="button" onClick={() => navigate(`/opportunities?q=${encodeURIComponent(rfq.opportunity_code || "")}`)} data-testid="rfq-opportunity-link"
                  className="mt-0.5 inline-flex items-center gap-1.5 font-medium text-brand underline-offset-2 hover:underline">
                  {rfq.opportunity_code || "View"}
                  <ExternalLink className="h-3.5 w-3.5 opacity-70" />
                </button>
              </div>
            )}
            {rfq.code && (() => {
              const turl = `${window.location.origin}/t/${encodeURIComponent(rfq.code)}`;
              return (
                <div data-testid="rfq-client-tracking">
                  <div className="mb-1 text-xs tracking-wide text-muted-foreground">Client Tracking Link</div>
                  <div className="mt-0.5 flex items-center gap-3">
                    <a href={turl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 font-medium text-brand underline-offset-2 hover:underline" data-testid="rfq-tracking-open">
                      Open<ExternalLink className="h-3.5 w-3.5 opacity-70" />
                    </a>
                    <button type="button" onClick={async () => { try { await navigator.clipboard.writeText(turl); toast.success("Tracking Link Copied"); } catch { window.prompt("Copy This Link", turl); } }}
                      className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-slate-600 hover:text-slate-900" data-testid="rfq-tracking-copy">
                      <Copy className="h-3.5 w-3.5" />Copy Link
                    </button>
                  </div>
                  <button type="button" onClick={copyAck} className="mt-1 inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-brand hover:underline" data-testid="rfq-ack-open">
                    <Copy className="h-3.5 w-3.5" />Copy Email Acknowledgement Draft
                  </button>
                </div>
              );
            })()}
          </div>
          <div>
            <div className="mb-2 text-xs tracking-wide text-muted-foreground">Pipeline Stage</div>
            <div className="flex flex-wrap items-center gap-2" data-testid="rfq-stage-stepper" role="group" aria-label="Pipeline stage">
              {RFQ_STAGES.map((s, i) => {
                const curIdx = RFQ_STAGES.findIndex((x) => x.value === (rfq.stage || "new"));
                const isActive = (rfq.stage || "new") === s.value;
                const isPast = i < curIdx && !["lost", "unquoted"].includes(s.value) && (rfq.stage || "new") !== "unquoted";
                const cls = isActive
                  ? "bg-brand text-white shadow-sm shadow-brand/30"
                  : isPast
                  ? "bg-ok/15 text-ok hover:bg-ok/25"
                  : "bg-steel-100 text-steel-500 hover:bg-steel-200";
                return (
                  <button
                    key={s.value}
                    onClick={() => (s.value === "won" ? setWonOpen(true) : s.value === "unquoted" ? setUqOpen(true) : setStage(s.value))}
                    aria-current={isActive ? "step" : undefined}
                    data-testid={`rfq-stage-${s.value}`}
                    className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${cls}`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
            {(uqOpen || rfq.stage === "unquoted") && (
              <UnquotedBox rfq={rfq} editing={uqOpen} onCancel={() => setUqOpen(false)} onEdit={() => setUqOpen(true)}
                onSave={async (reason, note) => { await saveRfqField({ stage: "unquoted", unquoted_reason: reason, unquoted_note: note }); setUqOpen(false); toast.success("Marked Unquoted"); }} />
            )}
          </div>
        </div>
      </div>
      )}

      <RFQWorkflow steps={wfSteps} currentIndex={wfCurrent} />

      {!rfq.is_stock && (<>
      {/* OneDrive / SharePoint folder link for this RFQ */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-white px-5 py-4" data-testid="rfq-onedrive-card">
        <div className="flex min-w-0 items-start gap-3">
          <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#E6ECF5] text-[#1B4383]"><FolderOpen className="h-4 w-4" /></span>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-800">OneDrive / SharePoint Folder</div>
            {odEditing ? (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Input autoFocus value={odValue} onChange={(e) => setOdValue(e.target.value)} placeholder="Paste the OneDrive folder link…" className="h-8 w-[22rem] max-w-full text-sm" data-testid="rfq-onedrive-input" />
                <Button size="sm" className="h-8" onClick={saveOnedrive} disabled={odSaving} data-testid="rfq-onedrive-save">{odSaving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}Save</Button>
                <Button size="sm" variant="ghost" className="h-8" onClick={() => setOdEditing(false)}>Cancel</Button>
              </div>
            ) : rfq.onedrive_url ? (
              <a href={rfq.onedrive_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex max-w-full items-center gap-1 rounded-md bg-[#E6ECF5] px-2.5 py-1 text-sm font-semibold text-[#1B4383] hover:bg-[#d8e1ef]" title={rfq.onedrive_url} data-testid="rfq-onedrive-link">
                <span className="truncate">Open RFQ Folder</span><ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-70" />
              </a>
            ) : (
              <div className="text-xs text-muted-foreground">No link set — add it to give the team one-click access to this RFQ's folder.</div>
            )}
            <div className="mt-2 flex min-w-0 flex-wrap items-center gap-2">
              <span className="text-[11px] font-semibold tracking-wide text-slate-500">Folder Name:</span>
              <span className="min-w-0 break-all font-mono text-xs text-slate-800" data-testid="rfq-folder-name">{rfqFolderName(rfq)}</span>
              <button type="button" onClick={() => { navigator.clipboard?.writeText(rfqFolderName(rfq)); toast.success("Folder Name Copied"); }} className="inline-flex items-center gap-1 rounded border border-border px-1.5 py-0.5 text-[11px] font-semibold text-[#1B4383] hover:bg-[#E6ECF5]" data-testid="rfq-folder-copy"><Copy className="h-3 w-3" />Copy</button>
            </div>
          </div>
        </div>
        {!odEditing && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => { setOdValue(rfq.onedrive_url || ""); setOdEditing(true); }} data-testid="rfq-onedrive-edit">
              {rfq.onedrive_url ? <><Pencil className="mr-1.5 h-4 w-4" />Edit</> : <><Plus className="mr-1.5 h-4 w-4" />Add Link</>}
            </Button>
          </div>
        )}
      </div>

      <div className="mb-6 grid gap-6 lg:grid-cols-3">
      <div className={`rounded-lg border bg-white p-5 lg:col-span-2 ${!items.length ? "border-[#EAD9A0] bg-[#FFFBEB] ring-1 ring-[#EAD9A0]" : "border-border"}`}>
        <div className="mb-4 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <h2 className="font-heading text-base font-bold tracking-tight">Extract Line Items from RFQ Source</h2>
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex h-auto flex-wrap justify-start gap-1" data-testid="source-tabs">
            <TabsTrigger value="text" data-testid="tab-text">Paste Email</TabsTrigger>
            <TabsTrigger value="pdf" data-testid="tab-pdf">Upload PDF</TabsTrigger>
            <TabsTrigger value="image" data-testid="tab-image">Upload Image</TabsTrigger>
            <TabsTrigger value="excel" data-testid="tab-excel">Upload Excel</TabsTrigger>
          </TabsList>
          <TabsContent value="text" className="mt-4">
            <Textarea rows={6} placeholder="Paste the client's RFQ email body here..." value={emailText} onChange={(e) => setEmailText(e.target.value)} data-testid="email-textarea" />
          </TabsContent>
          <TabsContent value="pdf" className="mt-4"><Input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files[0])} className={`${!file ? INPUT_YELLOW_RFQ : ""}`} data-testid="pdf-input" /></TabsContent>
          <TabsContent value="image" className="mt-4"><Input type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])} className={`${!file ? INPUT_YELLOW_RFQ : ""}`} data-testid="image-input" /></TabsContent>
          <TabsContent value="excel" className="mt-4"><Input type="file" accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(e) => setFile(e.target.files[0])} className={`${!file ? INPUT_YELLOW_RFQ : ""}`} data-testid="excel-input" /></TabsContent>
        </Tabs>
        <div className="mt-4">
          <Button onClick={extract} disabled={extracting} data-testid="extract-button">
            {extracting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
            {extracting ? "Extracting..." : "Extract with AI"}
          </Button>
        </div>
      </div>
        <SourceDocuments rfqId={id} reloadKey={srcKey} />
      </div>
      </>)}

      <Tabs value={mainTab} onValueChange={setMainTab} className="w-full">
        <TabsList className="grid h-auto w-full grid-cols-2 gap-2 bg-transparent p-0 sm:grid-cols-4 sm:gap-3" data-testid="rfq-main-tabs">
          <TabsTrigger value="items" data-testid="tab-line-items" className="flex h-auto min-h-[2.75rem] items-center justify-center gap-2 whitespace-normal rounded-lg border border-border bg-white px-3 py-2 text-center text-sm font-medium leading-tight text-slate-600 shadow-sm transition-colors data-[state=active]:border-[#1B4383] data-[state=active]:bg-[#1B4383] data-[state=active]:text-white data-[state=active]:shadow sm:min-h-[3.25rem] sm:py-3 sm:text-[15px]">
            <List className="h-4 w-4 shrink-0" />Line Items
          </TabsTrigger>
          <TabsTrigger value="quotes" data-testid="tab-quotes" className="flex h-auto min-h-[2.75rem] items-center justify-center gap-2 whitespace-normal rounded-lg border border-border bg-white px-3 py-2 text-center text-sm font-medium leading-tight text-slate-600 shadow-sm transition-colors data-[state=active]:border-[#1B4383] data-[state=active]:bg-[#1B4383] data-[state=active]:text-white data-[state=active]:shadow sm:min-h-[3.25rem] sm:py-3 sm:text-[15px]">
            <Calculator className="h-4 w-4 shrink-0" />{rfq.is_stock ? "Supplier Quotes" : <>Supplier Quotes &amp; Costing</>}
          </TabsTrigger>
          <TabsTrigger value="compare" data-testid="tab-compare" className="flex h-auto min-h-[2.75rem] items-center justify-center gap-2 whitespace-normal rounded-lg border border-border bg-white px-3 py-2 text-center text-sm font-medium leading-tight text-slate-600 shadow-sm transition-colors data-[state=active]:border-[#1B4383] data-[state=active]:bg-[#1B4383] data-[state=active]:text-white data-[state=active]:shadow sm:min-h-[3.25rem] sm:py-3 sm:text-[15px]">
            <GitCompare className="h-4 w-4 shrink-0" />Compare
          </TabsTrigger>
          {!rfq.is_stock && <TabsTrigger value="client-quote" data-testid="tab-client-quote" className="flex h-auto min-h-[2.75rem] items-center justify-center gap-2 whitespace-normal rounded-lg border border-border bg-white px-3 py-2 text-center text-sm font-medium leading-tight text-slate-600 shadow-sm transition-colors data-[state=active]:border-[#1B4383] data-[state=active]:bg-[#1B4383] data-[state=active]:text-white data-[state=active]:shadow sm:min-h-[3.25rem] sm:py-3 sm:text-[15px]">
            <FileText className="h-4 w-4 shrink-0" />Somcore Quote
          </TabsTrigger>}
        </TabsList>
        <TabsContent value="items">
      <SupplierSends rfqId={id} refreshKey={activityKey} />
      <div className="mt-4 rounded-lg border border-border bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <div className="flex items-center gap-4">
            <h2 className="font-heading text-base font-bold tracking-tight">Line Items</h2>
            <span className="text-sm text-muted-foreground" data-testid="assigned-count">{assignedCount}/{items.length} assigned</span>
            {items.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#E4EEE8] px-2.5 py-0.5 text-xs font-medium text-[#527D63]" data-testid="awarded-count">
                <Crown className="h-3 w-3" />{awardedItems}/{items.length} awarded
              </span>
            )}
            {awaitingDecision > 0 && (
              <span className="rounded-full bg-[#F6EDD9] px-2.5 py-0.5 text-xs font-medium text-[#A87F31]" data-testid="awaiting-count">
                {awaitingDecision} awaiting decision
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={() => setSendOpen(true)} disabled={!assignedCount} data-testid="send-to-suppliers-button">
              <Send className="mr-2 h-4 w-4" />Send to Suppliers
            </Button>
            <Button variant="outline" size="sm" onClick={() => setGrouped((g) => !g)} data-testid="group-toggle">
              {grouped ? <List className="mr-2 h-4 w-4" /> : <Layers className="mr-2 h-4 w-4" />}{grouped ? "Flat View" : "Group By Supplier"}
            </Button>
            {!grouped && <Button size="sm" variant="outline" onClick={addRow} data-testid="add-item-button"><Plus className="mr-2 h-4 w-4" />Add Row</Button>}
            {!rfq.is_stock && items.some((it) => !it.catalog_sku && it.line_type !== "from_stock") && (
              <Button size="sm" variant="outline" onClick={() => setBulkSku(true)} className="border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="bulk-sku-open"><Tag className="mr-2 h-4 w-4" />Link SKUs</Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={!items.length} data-testid="apply-supplier-all"><Layers className="mr-2 h-4 w-4" />Supplier to All</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="max-h-80 w-64 overflow-y-auto">
                <div className="p-1.5" onKeyDown={(e) => e.stopPropagation()}>
                  <div className="mb-1 text-xs font-medium text-slate-500">Apply a supplier to every line</div>
                  <div className="flex items-center gap-1">
                    <Input value={bulkName} onChange={(e) => setBulkName(e.target.value)} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") { e.preventDefault(); createAndApplyToAll(bulkName.trim()); setBulkName(""); } }} placeholder="New supplier name…" className="h-7 text-xs" data-testid="apply-all-new-name" />
                    <Button size="sm" className="h-7 shrink-0 px-2 text-xs" disabled={!bulkName.trim()} data-testid="apply-all-create" onClick={(e) => { e.preventDefault(); e.stopPropagation(); createAndApplyToAll(bulkName.trim()); setBulkName(""); }}>Create</Button>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs">Existing suppliers</DropdownMenuLabel>
                {suppliers.map((s) => <DropdownMenuItem key={s.id} onClick={() => applySupplierToAll(s.id)} data-testid={`apply-all-${s.id}`}>{s.name}</DropdownMenuItem>)}
                {!suppliers.length && <div className="px-2 py-1.5 text-xs text-muted-foreground">No suppliers yet — create one above.</div>}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {selectedIds.size > 0 && !grouped && (
          <div className="flex flex-wrap items-center gap-2 border-b border-border bg-[#EEF3FB] px-5 py-3" data-testid="selection-bar">
            <span className="text-sm font-semibold text-[#1B4383]" data-testid="selection-count">{selectedIds.size} selected</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" data-testid="assign-selected"><Plus className="mr-2 h-4 w-4" />Assign Supplier</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-h-80 w-64 overflow-y-auto">
                <div className="p-1.5" onKeyDown={(e) => e.stopPropagation()}>
                  <div className="mb-1 text-xs font-medium text-slate-500">Apply to {selectedIds.size} selected line{selectedIds.size === 1 ? "" : "s"}</div>
                  <div className="flex items-center gap-1">
                    <Input value={selBulkName} onChange={(e) => setSelBulkName(e.target.value)} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") { e.preventDefault(); createAndApplyToSelected(selBulkName.trim()); setSelBulkName(""); } }} placeholder="New supplier name…" className="h-7 text-xs" data-testid="assign-selected-new-name" />
                    <Button size="sm" className="h-7 shrink-0 px-2 text-xs" disabled={!selBulkName.trim()} data-testid="assign-selected-create" onClick={(e) => { e.preventDefault(); e.stopPropagation(); createAndApplyToSelected(selBulkName.trim()); setSelBulkName(""); }}>Create</Button>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs">Existing suppliers</DropdownMenuLabel>
                {suppliers.map((s) => <DropdownMenuItem key={s.id} onClick={() => applySupplierToSelected(s.id)} data-testid={`assign-selected-${s.id}`}>{s.name}</DropdownMenuItem>)}
                {!suppliers.length && <div className="px-2 py-1.5 text-xs text-muted-foreground">No suppliers yet — create one above.</div>}
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="remove-selected"><Trash2 className="mr-2 h-4 w-4" />Remove Supplier</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-h-80 w-64 overflow-y-auto">
                <DropdownMenuLabel className="text-xs">Remove from selected line{selectedIds.size === 1 ? "" : "s"}</DropdownMenuLabel>
                {selectedSupplierOptions.map((s) => <DropdownMenuItem key={s.id} onClick={() => removeSupplierFromSelected(s.id)} data-testid={`remove-selected-${s.id}`}>{s.name}</DropdownMenuItem>)}
                {!selectedSupplierOptions.length && <div className="px-2 py-1.5 text-xs text-muted-foreground">No suppliers on selected lines.</div>}
                {selectedSupplierOptions.length > 0 && <><DropdownMenuSeparator /><DropdownMenuItem className="text-destructive focus:text-destructive" onClick={removeAllFromSelected} data-testid="remove-selected-all">Remove all suppliers</DropdownMenuItem></>}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="ghost" size="sm" onClick={clearSelection} data-testid="clear-selection">Clear</Button>
          </div>
        )}

        {!grouped ? (
          <>
            {/* Desktop table */}
            <div className="hidden overflow-x-auto sm:block">
              <table className="data-table w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-left text-xs tracking-wide text-slate-500">
                    <th className="px-3 py-3 w-10 text-center">
                      <Checkbox checked={allSelected ? true : selectedIds.size > 0 ? "indeterminate" : false} onCheckedChange={toggleSelectAll} data-testid="select-all-items" aria-label="Select all" />
                    </th>
                    <th className="px-3 py-3 font-medium text-center">S/N</th>
                    <th className="px-3 py-3 font-medium">Item Description</th>
                    <th className="px-3 py-3 font-medium">Part Number (If Any)</th>
                    <th className="px-3 py-3 font-medium">Quantity</th>
                    <th className="px-3 py-3 font-medium">Catalog SKU</th>
                    <th className="px-3 py-3 font-medium">Suppliers</th>
                    <th className="px-3 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => (
                    <ItemRow key={it.id} onReload={rfq.is_stock ? null : loadItems} item={it} index={idx + 1} suppliers={suppliers} onSaveFields={saveItemFields}
                      onAddSupplier={addSupplier} onCreateSupplier={createAndAddSupplier} onStatus={setLinkStatus}
                      onEdit={(link) => setQuoteDialog({ link, price: link.quoted_price ?? "", notes: link.notes ?? "" })}
                      onOpenWorkspace={(link) => navigate(`/rfqs/${id}/quotes/${link.supplier_id}`)}
                      onOpenSku={(item) => { setSkuPick(item); setSkuSearch(""); }} onLinkSku={linkSku}
                      catalogById={catalogById}
                      selected={selectedIds.has(it.id)} onToggleSelect={toggleSelect}
                      onRemoveLink={removeLink} onDelete={deleteRow} />
                  ))}
                  {!items.length && <tr><td colSpan={8} className="px-5 py-10 text-center text-muted-foreground">No items yet. Extract from a source above or add rows manually.</td></tr>}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="divide-y divide-border sm:hidden">
              {items.map((it, idx) => (
                <ItemCard key={it.id} onReload={rfq.is_stock ? null : loadItems} item={it} index={idx + 1} suppliers={suppliers} onSaveFields={saveItemFields}
                  onAddSupplier={addSupplier} onCreateSupplier={createAndAddSupplier} onStatus={setLinkStatus}
                  onEdit={(link) => setQuoteDialog({ link, price: link.quoted_price ?? "", notes: link.notes ?? "" })}
                  onOpenWorkspace={(link) => navigate(`/rfqs/${id}/quotes/${link.supplier_id}`)}
                  onOpenSku={(item) => { setSkuPick(item); setSkuSearch(""); }} onLinkSku={linkSku}
                  selected={selectedIds.has(it.id)} onToggleSelect={toggleSelect}
                  onRemoveLink={removeLink} onDelete={deleteRow} />
              ))}
              {!items.length && <div className="px-5 py-10 text-center text-muted-foreground">No items yet. Extract from a source above or add rows manually.</div>}
            </div>
          </>
        ) : (
          <div className="divide-y divide-border">
            {Object.keys(groups).length === 0 && <div className="px-5 py-10 text-center text-muted-foreground">No suppliers attached yet.</div>}
            {Object.entries(groups).map(([sid, g]) => (
              <div key={sid} className="p-5" data-testid={`group-${sid}`}>
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <div className="font-heading font-bold text-slate-900">{g.name}</div>
                    <div className="text-xs text-muted-foreground">{(g.categories && g.categories.length ? g.categories.map((c) => labelFor(c, SUPPLIER_CATEGORIES)).join(", ") : "—")} · {g.rows.length} item(s)</div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setSendOpen(true)} data-testid={`send-suppliers-${sid}`}>
                      <Send className="mr-2 h-4 w-4" />Send to Supplier
                    </Button>
                  </div>
                </div>
                <table className="data-table w-full table-fixed text-sm rounded border border-border">
                  <colgroup>
                    <col className="w-[6%]" />
                    <col className="w-[40%]" />
                    <col className="w-[18%]" />
                    <col className="w-[16%]" />
                    <col className="w-[20%]" />
                  </colgroup>
                  <thead>
                    <tr className="bg-slate-50 text-left text-xs tracking-wide text-slate-500">
                      <th className="px-3 py-2 font-medium text-center">S/N</th>
                      <th className="px-3 py-2 font-medium">Item Description</th>
                      <th className="px-3 py-2 font-medium">Part Number (If Any)</th>
                      <th className="px-3 py-2 font-medium">Quantity</th>
                      <th className="px-3 py-2 font-medium text-center">This Quote</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.map(({ item, link }, ri) => (
                      <tr key={link.id} className="border-t border-border">
                        <td className="px-3 py-2 text-center font-mono text-xs text-muted-foreground">{ri + 1}</td>
                        <td className="px-3 py-2">{item.description}</td>
                        <td className="px-3 py-2 font-mono text-xs">{item.spec_or_part || "—"}</td>
                        <td className="px-3 py-2 font-mono">{item.quantity ?? ""}{item.unit ? ` ${item.unit}` : ""}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${LINK_STYLES[link.status]}`}>
                            {link.status === "quoted" && <Check className="h-3 w-3" />}
                            {LINK_STATUSES.find((s) => s.value === link.status)?.label}
                            {link.quoted_price != null && ` · $${link.quoted_price}`}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>

      {grouped && unassignedItems.length > 0 && (
        <p className="mt-3 text-sm text-amber-600" data-testid="assign-warning">{unassignedItems.length} item(s) have no supplier attached yet and won't appear in any export.</p>
      )}
        </TabsContent>
        <TabsContent value="quotes">
          <QuotesPanel rfqId={id} onChanged={() => { loadItems(); refreshRfq(); }} />
        </TabsContent>
        <TabsContent value="compare">
          <ComparePanel rfqId={id} onChanged={() => { loadItems(); refreshRfq(); bumpActivity(); }} />
        </TabsContent>
        <TabsContent value="client-quote">
          <ClientQuotePanel rfqId={id} onGotoCosting={() => setMainTab("quotes")} onChanged={() => { loadItems(); refreshRfq(); bumpActivity(); }} />
        </TabsContent>
      </Tabs>

      <div className="mt-6">
        <ActivityLog entityType="rfqs" entityId={id} refreshKey={`${activityKey}-${items.length}-${rfq?.stage}`} showStage />
      </div>

      <SendToSuppliers rfqId={id} open={sendOpen} onOpenChange={setSendOpen} onSent={() => { loadItems(); bumpActivity(); }} />

      <Dialog open={wonOpen} onOpenChange={setWonOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="rfq-won-dialog">
          <DialogHeader><DialogTitle>Mark As Won — Capture The Client PO</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">This creates the order. Enter the client's PO details now (you can also add them later on the order).</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Client PO Number</label>
              <Input value={wonForm.client_po_ref} onChange={(e) => setWonForm({ ...wonForm, client_po_ref: e.target.value })} placeholder="e.g. PO-4523" data-testid="won-po-ref" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Client PO Date</label>
              <Input type="date" value={wonForm.client_po_date} onChange={(e) => setWonForm({ ...wonForm, client_po_date: e.target.value })} data-testid="won-po-date" />
            </div>
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-2 sm:space-x-0">
            <Button variant="ghost" onClick={() => setWonOpen(false)} data-testid="won-skip" className="w-full sm:w-auto sm:mr-auto">Skip For Now</Button>
            <Button onClick={() => confirmWon(true)} disabled={wonBusy} data-testid="won-confirm" className="w-full bg-[#1B4383] text-white hover:bg-[#16386e] sm:w-auto">{wonBusy ? "Saving..." : "Mark Won & Open Order"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!quoteDialog} onOpenChange={(o) => !o && setQuoteDialog(null)}>
        <DialogContent data-testid="quote-dialog">
          <DialogHeader><DialogTitle>Quote details — {quoteDialog?.link.supplier_name}</DialogTitle></DialogHeader>
          {quoteDialog && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={quoteDialog.link.status} onValueChange={(v) => setQuoteDialog({ ...quoteDialog, link: { ...quoteDialog.link, status: v } })}>
                  <SelectTrigger data-testid="quote-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{LINK_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Quoted Price (optional)</Label>
                <Input type="number" value={quoteDialog.price} onChange={(e) => setQuoteDialog({ ...quoteDialog, price: e.target.value })} placeholder="e.g. 1250.00" data-testid="quote-price" />
              </div>
              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea rows={3} value={quoteDialog.notes} onChange={(e) => setQuoteDialog({ ...quoteDialog, notes: e.target.value })} data-testid="quote-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuoteDialog(null)}>Cancel</Button>
            <Button onClick={saveQuote} data-testid="quote-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkSkuLinkDialog open={bulkSku} onClose={() => setBulkSku(false)} items={items} catalog={catalog} onLink={linkSkuQuiet} />
      <Dialog open={!!skuPick} onOpenChange={(o) => { if (!o) { setSkuPick(null); setSkuSearch(""); setSkuCreate(false); } }}>
        <DialogContent className="max-w-lg" data-testid="sku-picker-dialog">
          <DialogHeader><DialogTitle>{skuCreate ? "Create & Link a New SKU" : "Link a Catalog SKU"}</DialogTitle></DialogHeader>
          <p className="-mt-1 text-sm text-muted-foreground truncate">For: <span className="font-medium text-slate-700">{skuPick?.description || "line item"}</span></p>
          {skuCreate ? (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Name / Description *</Label>
                <Input autoFocus value={newProd.name} onChange={(e) => setNewProd({ ...newProd, name: e.target.value })} onBlur={(e) => setNewProd((p) => ({ ...p, name: tc(e.target.value) }))} data-testid="sku-new-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">Category</Label>
                  <CategoryCombo value={newProd.category} onChange={(v) => setNewProd({ ...newProd, category: v })} testid="sku-new-category" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">Unit of Measure</Label>
                  <Input value={newProd.uom} onChange={(e) => setNewProd({ ...newProd, uom: e.target.value })} placeholder="EA" data-testid="sku-new-uom" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">Brand / OEM</Label>
                  <BrandCombo value={newProd.brand} onChange={(v) => setNewProd({ ...newProd, brand: v })} testid="sku-new-brand" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">Manufacturer Part No.</Label>
                  <Input value={newProd.mpn} onChange={(e) => setNewProd({ ...newProd, mpn: e.target.value })} data-testid="sku-new-mpn" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Default Supplier</Label>
                <Select value={newProd.default_supplier_id || "none"} onValueChange={(v) => setNewProd({ ...newProd, default_supplier_id: v })}>
                  <SelectTrigger data-testid="sku-new-supplier"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <LinkBrandToggle brand={newProd.brand} supplier={suppliers.find((s) => s.id === newProd.default_supplier_id)} checked={newProd.link_brand} onChange={(v) => setNewProd({ ...newProd, link_brand: v })} />
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setSkuCreate(false)} data-testid="sku-create-back">Back to search</Button>
                <Button onClick={createAndLinkSku} className="bg-[#1B4383] hover:bg-[#16386e]" data-testid="sku-create-link">Create &amp; Link</Button>
              </DialogFooter>
            </div>
          ) : (
            <>
              <Input autoFocus value={skuSearch} onChange={(e) => setSkuSearch(e.target.value)} placeholder="Search SKU, name, brand or part no…" data-testid="sku-picker-search" />
              <div className="max-h-[280px] space-y-1 overflow-y-auto">
                {catalog.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No catalog products yet — create one below.</p>}
                {catalog
                  .filter((p) => { const q = skuSearch.trim().toLowerCase(); return !q || `${p.sku} ${p.name} ${p.brand} ${p.mpn} ${p.category}`.toLowerCase().includes(q); })
                  .slice(0, 50)
                  .map((p) => (
                    <button key={p.id} onClick={() => linkSku(skuPick.id, p.id)} className="flex w-full items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-left hover:bg-slate-50" data-testid={`sku-option-${p.id}`}>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-slate-800">{p.name}</div>
                        <div className="truncate text-xs text-muted-foreground">{[p.brand, p.mpn, p.category].filter(Boolean).join(" · ") || "—"}</div>
                      </div>
                      <span className="shrink-0 rounded bg-[#E6ECF5] px-1.5 py-0.5 font-mono text-[10px] font-semibold text-[#1B4383]">{p.sku}</span>
                    </button>
                  ))}
              </div>
              <Button variant="outline" onClick={startSkuCreate} className="w-full border-dashed" data-testid="sku-open-create"><Plus className="mr-1.5 h-4 w-4" />Create a new product &amp; link it</Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

function UnquotedBox({ rfq, editing, onSave, onCancel, onEdit }) {
  const [reason, setReason] = useState(rfq.unquoted_reason || "");
  const [note, setNote] = useState(rfq.unquoted_note || "");
  const [busy, setBusy] = useState(false);
  if (!editing) {
    return (
      <div className="mt-3 max-w-md rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="rfq-unquoted-reason-view">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0"><div className="text-[11px] font-semibold tracking-wide text-slate-500">Reason Unquoted</div><div className="font-medium text-slate-800">{rfq.unquoted_reason || "Not Given"}</div>{rfq.unquoted_note && <div className="mt-0.5 text-xs text-slate-600">{rfq.unquoted_note}</div>}</div>
          <button type="button" onClick={onEdit} className="shrink-0 text-xs font-semibold text-[#1B4383] hover:underline" data-testid="rfq-unquoted-edit">Edit</button>
        </div>
      </div>
    );
  }
  return (
    <div className="mt-3 max-w-md space-y-2 rounded-lg border border-amber-200 bg-amber-50/60 p-3" data-testid="rfq-unquoted-box">
      <div className="text-xs font-semibold text-slate-700">Why Is This RFQ Unquoted?</div>
      <Select value={reason} onValueChange={setReason}>
        <SelectTrigger className="h-9 bg-white" data-testid="rfq-unquoted-reason"><SelectValue placeholder="Select A Reason" /></SelectTrigger>
        <SelectContent>{UNQUOTED_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
      </Select>
      <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={reason === "Other" ? "Please Explain" : "Details (Optional)"} rows={2} className="bg-white" data-testid="rfq-unquoted-note" />
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} data-testid="rfq-unquoted-cancel">Cancel</Button>
        <Button size="sm" disabled={busy || !reason || (reason === "Other" && !note.trim())} onClick={async () => { setBusy(true); try { await onSave(reason, note.trim()); } finally { setBusy(false); } }} className="bg-[#1B4383] hover:bg-[#16386e]" data-testid="rfq-unquoted-save">Mark Unquoted</Button>
      </div>
    </div>
  );
}

function Meta({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-0.5 font-medium text-slate-900 ${mono ? "font-mono text-sm" : ""}`}>{value}</div>
    </div>
  );
}
